import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/shared/utils';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Label } from '@/shared/components/ui/label';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import {
  Key, Eye, EyeOff, Save, Trash2, CheckCircle, AlertTriangle,
  RefreshCw, ExternalLink, Info, Shield, Cloud, Map, Navigation,
  Database, HardDrive, Lock, Unlock, Edit2, Plus, Check, X
} from 'lucide-react';

const API_URL = API_BASE;

// Provider icons and colors
const PROVIDER_CONFIG = {
  google: { icon: Map, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  here: { icon: Navigation, color: 'text-green-500', bg: 'bg-green-500/10' },
  mapbox: { icon: Map, color: 'text-violet-500', bg: 'bg-violet-500/10' },
  openroute: { icon: Navigation, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  aws: { icon: Cloud, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  gcs: { icon: Database, color: 'text-blue-500', bg: 'bg-blue-500/10' },
};

// Category configurations
const CATEGORY_CONFIG = {
  traffic: { label: 'Traffic & Routing', icon: Navigation, color: 'text-blue-500' },
  storage: { label: 'Cloud Storage', icon: Cloud, color: 'text-orange-500' },
  notification: { label: 'Notifications', icon: Info, color: 'text-green-500' },
  payment: { label: 'Payments', icon: Shield, color: 'text-violet-500' },
  other: { label: 'Other', icon: Key, color: 'text-gray-500' },
};

// API Key Input Component with visibility toggle
const APIKeyInput = ({ 
  value, 
  onChange, 
  placeholder = "Enter API key...",
  disabled = false,
  isSecret = true,
  onSave,
  onCancel,
  showActions = false,
  saving = false
}) => {
  const [visible, setVisible] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');

  // Reset local value when value prop changes and editing starts fresh
  const currentValue = showActions ? localValue : (value || '');

  const handleChange = (e) => {
    const newVal = e.target.value;
    setLocalValue(newVal);
    if (onChange) onChange(newVal);
  };

  const handleStartEdit = () => {
    setLocalValue('');
  };

  return (
    <div className="space-y-2">
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <Input
            type={visible || !isSecret ? "text" : "password"}
            value={currentValue}
            onChange={handleChange}
            onFocus={handleStartEdit}
            placeholder={placeholder}
            disabled={disabled}
            className="pr-10 font-mono text-sm"
            data-testid="api-key-input"
          />
          {isSecret && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 hover:bg-muted"
              onClick={() => setVisible(!visible)}
              tabIndex={-1}
              data-testid="toggle-visibility-btn"
            >
              {visible ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          )}
        </div>
        {showActions && (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              onClick={() => onSave && onSave(localValue)}
              disabled={saving || !localValue}
              className="h-9"
              data-testid="save-key-btn"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
            </Button>
            {onCancel && (
              <Button
                size="sm"
                variant="outline"
                onClick={onCancel}
                className="h-9"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// API Key Card Component
const APIKeyCard = ({ keyDef, onEdit, onDelete, onValidate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [newValue, setNewValue] = useState('');
  const { token } = useAuth();

  const providerConfig = PROVIDER_CONFIG[keyDef.provider] || { icon: Key, color: 'text-gray-500', bg: 'bg-gray-500/10' };
  const ProviderIcon = providerConfig.icon;

  const handleSave = async (value) => {
    if (!value) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/api-keys/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          key_name: keyDef.key_name,
          value: value
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setEditing(false);
        setNewValue('');
        if (onEdit) onEdit();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save API key');
      }
    } catch (error) {
      toast.error('Failed to save API key');
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    if (!keyDef.is_configured) {
      toast.error('Please save an API key first');
      return;
    }

    setValidating(true);
    try {
      const response = await fetch(`${API_URL}/api/api-keys/${keyDef.key_name}/validate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.error || 'Validation failed');
      }
      if (onValidate) onValidate();
    } catch (error) {
      toast.error('Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete ${keyDef.display_name}? This cannot be undone.`)) return;

    try {
      const response = await fetch(`${API_URL}/api/api-keys/${keyDef.key_name}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success(`${keyDef.display_name} deleted`);
        if (onDelete) onDelete();
      } else {
        toast.error('Failed to delete API key');
      }
    } catch (error) {
      toast.error('Failed to delete API key');
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`api-key-card-${keyDef.key_name}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Provider Icon */}
          <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center shrink-0', providerConfig.bg)}>
            <ProviderIcon className={cn('h-5 w-5', providerConfig.color)} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-sm">{keyDef.display_name}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{keyDef.description}</p>
              </div>
              <div className="flex items-center gap-1">
                {keyDef.is_configured ? (
                  <Badge variant="outline" className="text-emerald-500 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Configured
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-500 text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Not Set
                  </Badge>
                )}
                {keyDef.is_validated && (
                  <Badge variant="outline" className="text-blue-500 text-xs">
                    <Check className="h-3 w-3 mr-1" />
                    Valid
                  </Badge>
                )}
              </div>
            </div>

            {/* Current Value / Edit Mode */}
            <div className="mt-3">
              {editing ? (
                <APIKeyInput
                  value={newValue}
                  onChange={setNewValue}
                  placeholder={keyDef.masked_value || `Enter ${keyDef.display_name}...`}
                  isSecret={keyDef.is_secret !== false}
                  showActions={true}
                  onSave={handleSave}
                  onCancel={() => { setEditing(false); setNewValue(''); }}
                  saving={saving}
                />
              ) : keyDef.is_configured ? (
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-muted rounded text-xs font-mono truncate">
                    {keyDef.masked_value}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(true)}
                    className="h-8"
                    data-testid="edit-key-btn"
                  >
                    <Edit2 className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(true)}
                  className="w-full justify-start text-muted-foreground"
                  data-testid="add-key-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add API Key
                </Button>
              )}
            </div>

            {/* Actions */}
            {keyDef.is_configured && !editing && (
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleValidate}
                  disabled={validating}
                  className="h-7 text-xs"
                  data-testid="validate-key-btn"
                >
                  {validating ? (
                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle className="h-3 w-3 mr-1" />
                  )}
                  Validate
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDelete}
                  className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-500/10"
                  data-testid="delete-key-btn"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Delete
                </Button>
                {keyDef.docs_url && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs ml-auto"
                    onClick={() => window.open(keyDef.docs_url, '_blank')}
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Docs
                  </Button>
                )}
              </div>
            )}

            {/* Validation status */}
            {keyDef.last_validated_at && (
              <p className="text-xs text-muted-foreground mt-2">
                Last validated: {new Date(keyDef.last_validated_at).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Main API Key Management Component
export default function APIKeyManagement() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [definitions, setDefinitions] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [activeCategory, setActiveCategory] = useState('traffic');

  const fetchDefinitions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/api-keys/definitions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDefinitions(data.definitions);
        setGrouped(data.grouped);
      }
    } catch (error) {
      console.error('Error fetching API key definitions:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDefinitions();
  }, [fetchDefinitions]);

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You need administrator privileges to manage API keys.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const categories = Object.keys(grouped);
  const configuredCount = definitions.filter(d => d.is_configured).length;
  const validatedCount = definitions.filter(d => d.is_validated).length;

  return (
    <div className="p-6 space-y-6" data-testid="api-key-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Key className="h-7 w-7 text-primary" />
            API Key Management
          </h1>
          <p className="text-muted-foreground">
            Securely manage API keys for external services
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm">
            <CheckCircle className="h-4 w-4 mr-1 text-emerald-500" />
            {configuredCount}/{definitions.length} Configured
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchDefinitions}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Info Alert */}
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertDescription>
          API keys are encrypted and stored securely. Use the <Eye className="inline h-3 w-3" /> button to reveal keys when editing.
          Keys are applied immediately after saving.
        </AlertDescription>
      </Alert>

      {/* Tabs by Category */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          {categories.map(cat => {
            const catConfig = CATEGORY_CONFIG[cat] || CATEGORY_CONFIG.other;
            const CatIcon = catConfig.icon;
            const catKeys = grouped[cat] || [];
            const configuredInCat = catKeys.filter(k => k.is_configured).length;
            
            return (
              <TabsTrigger key={cat} value={cat} className="gap-2">
                <CatIcon className={cn('h-4 w-4', catConfig.color)} />
                {catConfig.label}
                <Badge variant="secondary" className="ml-1 text-xs">
                  {configuredInCat}/{catKeys.length}
                </Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {categories.map(cat => (
          <TabsContent key={cat} value={cat} className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="grid gap-4">
                {(grouped[cat] || []).map(keyDef => (
                  <APIKeyCard
                    key={keyDef.key_name}
                    keyDef={keyDef}
                    onEdit={fetchDefinitions}
                    onDelete={fetchDefinitions}
                    onValidate={fetchDefinitions}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Help Section */}
      <Card className="border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Info className="h-4 w-4" />
            How to Get API Keys
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">Traffic & Routing APIs</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• <strong>Google Maps:</strong> console.cloud.google.com → APIs & Services</li>
                <li>• <strong>HERE:</strong> developer.here.com → Create project</li>
                <li>• <strong>Mapbox:</strong> account.mapbox.com → Access tokens</li>
                <li>• <strong>OpenRoute:</strong> openrouteservice.org/dev (free tier)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Cloud Storage</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• <strong>AWS S3:</strong> IAM Console → Create access key</li>
                <li>• <strong>GCS:</strong> Cloud Console → Service accounts → Create key</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
